import { Layer, Source } from 'react-map-gl';
import { useMemo } from 'react';
import { Feature } from 'geojson';
import { defaultParkPinLayerLayoutProps, defaultSymbolLayerLayoutProps, getPolygonFillId, getUnclusteredPinsId } from '../../utils/layers';
import { useSelector } from '../../redux';
import useMap from '../../hooks/useMap';
import useSvgImages, { SvgDefinition } from '../../utils/useSvgImages';
import { coordinatesToSerializedCoordinates } from '../../utils/serializedCoordinates';
import useParkPinMouseEvents from '../../hooks/useParkPinMouseEvents';
import { nationalParkPinSvg } from '../../utils/svgDefinitions/nationalParkPinSvg';
import { defaultParkPinSvg } from '../../utils/svgDefinitions/defaultParkPinSvg';

const svgDefinitions: SvgDefinition[] = [nationalParkPinSvg, defaultParkPinSvg];

type Props = {
  uniqueId: number;
};

/**
 * @deprecated Use the ParkResults component and related redux variables
 */
const ParkPins = ({ uniqueId }: Props) => {
  const map = useMap();
  const sourceId = 'park';
  const { allParkCoordinates, parkIdsBySerializedCoordinates, parksById, allowClickEvents } = useSelector(state => ({
    allParkCoordinates: state.map.allParkCoordinates,
    parkIdsBySerializedCoordinates: state.map.parkIdsBySerializedCoordinates,
    parksById: state.map.parksById,
    allowClickEvents: state.map.allowClickEvents
  }));

  useParkPinMouseEvents({ fillLayerId: getPolygonFillId(uniqueId), map, allowClickEvents });

  const features = useMemo(() => {
    const features: Feature[] = [];

    (allParkCoordinates ?? []).forEach((coords, index) => {
      const { lat, lng } = coords;

      const serializedCoordinates = coordinatesToSerializedCoordinates(coords);
      const parkIds = parkIdsBySerializedCoordinates[serializedCoordinates];
      const type = parksById[parkIds[0]]?.type;

      const feature = {
        id: index,
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: { serializedCoordinates, type }
      } as Feature;

      features.push(feature);
    });
    return features;
  }, [allParkCoordinates, parkIdsBySerializedCoordinates, parksById]);

  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  if (!imagesAreLoaded || !features || features.length === 0) {
    return null;
  }

  return (
    <Source
      id={sourceId}
      type="geojson"
      data={{
        type: 'FeatureCollection',
        features
      }}
    >
      <Layer
        type="symbol"
        id={getUnclusteredPinsId('park')}
        source={sourceId}
        layout={{
          ...defaultSymbolLayerLayoutProps,
          ...defaultParkPinLayerLayoutProps,
          'icon-image': ['case', ['==', ['get', 'type'], 'N'], nationalParkPinSvg.name, defaultParkPinSvg.name]
        }}
      />
    </Source>
  );
};

export default ParkPins;
